import { FC, useContext } from "react";

import {
  History,
  Settings,
  VideoCall,
  VideoLibrary,
} from "@mui/icons-material";
import {
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";

import { collapsedAppIconSx } from "./collapsedAppIconSx";
import { frameNavItemSx } from "./frameNavItemSx";

interface Props {
  dense?: boolean;
  logout?: () => void;
  onMenuClick?: () => void;
  collapsed?: boolean;
}

const RubiConnectMenu: FC<Props> = ({ dense = false, collapsed = false }) => {
  const theme = useTheme();
  const { t } = useTranslation("connect");
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const { setCollapsed } = useSidebar();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgID = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";
  const perms = userPermissionChecks(
    currentUser?.permissions?.map((p) => p.title) ?? [],
  );

  const hideSideBar = () => {
    setCollapsed(true);
  };

  const menuItems = [
    {
      to: `/rubiconnect/${orgID}/occurrences`,
      text: t("menu.occurrences"),
      icon: <VideoCall />,
    },
    {
      to: `/rubiconnect/${orgID}/history`,
      text: t("menu.callHistory"),
      icon: <History />,
    },
    ...(perms.canManageConnectSettings
      ? [
          {
            to: `/rubiconnect/${orgID}/settings`,
            text: t("menu.settings"),
            icon: <Settings />,
          },
        ]
      : []),
  ];

  return (
    <div className="menu">
      {!collapsed ? (
        <div className="sidebar-top">
          <div className="app-name">
            Rubi<span>CONNECT</span>
          </div>
        </div>
      ) : (
        <div className="application-item">
          <Tooltip title="RubiCONNECT" placement="right" arrow>
            <IconButton
              aria-label="RubiCONNECT"
              size="large"
              disableRipple
              sx={collapsedAppIconSx}
            >
              <VideoLibrary />
            </IconButton>
          </Tooltip>
        </div>
      )}
      <div className="left-menu">
        {menuItems.map((item) => (
          <Tooltip
            key={item.to}
            title={collapsed ? item.text : ""}
            placement="right"
            arrow
          >
            <MenuItem
              component={NavLink}
              to={item.to}
              onClick={isSmall ? hideSideBar : undefined}
              sx={[
                frameNavItemSx(),
                {
                  pl: collapsed ? 1 : 2,
                  py: dense ? 1 : 1.5,
                  minHeight: collapsed ? 48 : "auto",
                  justifyContent: collapsed ? "center" : "flex-start",
                },
              ]}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={item.text} />}
            </MenuItem>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default RubiConnectMenu;
